/**
 * The signed-in customer's home: the current formula (approved if they have one, else in review),
 * every past version, and what changed between them (ACC-001, ACC-002).
 */
import * as stylex from "@stylexjs/stylex";
import { useEffect, useState } from "react";
import type { AccountFormula, AccountFormulas, AccountFormulaStatus, AccountFormulaVersion, AccountIngredient, FormulaChange } from "@lattice/core";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { border, radius, space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import { paper } from "@lattice/tokens/themes";
import { Button } from "@lattice/ui/Button";
import { LinkButton } from "@lattice/ui/LinkButton";
import { Pending } from "@lattice/ui/Pending";
import { Status } from "@lattice/ui/Status";
import { Text } from "@lattice/ui/Text";
import { AccountShell } from "./account/Shell.tsx";
import { loadAccountFormulas } from "./api.ts";
const WIDE = "@media (min-width: 960px)";
const DOSE = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 2
});
const DAY = new Intl.DateTimeFormat("en-US", {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC"
});
const styles = {
  sheet: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "x15agyyk",
    kmVPX3: "xxjejwv",
    kaIpWk: "x1fbwzx",
    kVAM5u: "xtkjqb",
    ksu8eU: "x1y0btm7",
    kMzoRj: "xdq7b4a",
    kWkggS: "xcqm4px",
    $$css: true
  }
};
type Home = AccountFormulas | "loading" | "signed_out" | "failed";
function formatDose(dose: number, unit: string): string {
  return `${DOSE.format(dose)} ${unit}`;
}
function day(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : DAY.format(date);
}
function baseLine(formula: AccountFormula): string | null {
  if (formula.proteinBase !== null && formula.flavor !== null) return `${formula.proteinBase} in ${formula.flavor}`;
  return formula.proteinBase;
}
function signedLine(formula: AccountFormula): string {
  const who = formula.physicianName ?? "your physician";
  if (formula.approvedAt === null) return `Signed by ${who}.`;
  return `Signed by ${who} on ${day(formula.approvedAt)}.`;
}
function statusLabel(status: AccountFormulaStatus): string {
  switch (status) {
    case "approved":
      return "Signed";
    case "in_review":
      return "In review";
    case "declined":
      return "Not approved";
    case "superseded":
      return "Replaced";
    case "draft":
      return "Draft";
  }
}
function changeLine(change: FormulaChange): string {
  switch (change.kind) {
    case "added":
      return change.toDose === null ? `Added ${change.name}` : `Added ${change.name}, ${formatDose(change.toDose, change.unit)}`;
    case "removed":
      return `Removed ${change.name}`;
    case "dose_changed":
      return doseChange(change);
  }
}
function doseChange(change: FormulaChange): string {
  const from = change.fromDose === null ? "" : formatDose(change.fromDose, change.unit);
  const to = change.toDose === null ? "" : formatDose(change.toDose, change.unit);
  if (from === "" || to === "") return `${change.name} dose changed`;
  return `${change.name} ${from} to ${to}`;
}
function flavorNote(version: AccountFormulaVersion, older: AccountFormulaVersion | undefined): string | null {
  if (older === undefined || version.flavor === older.flavor) return null;
  if (version.flavor === null) return "Flavor removed";
  if (older.flavor === null) return `Flavor set to ${version.flavor}`;
  return `Flavor ${older.flavor} to ${version.flavor}`;
}
function proteinNote(version: AccountFormulaVersion, older: AccountFormulaVersion | undefined): string | null {
  if (older === undefined || version.proteinBase === older.proteinBase) return null;
  if (version.proteinBase === null) return "Protein base removed";
  if (older.proteinBase === null) return `Protein set to ${version.proteinBase}`;
  return `${older.proteinBase} to ${version.proteinBase}`;
}
function IngredientRow({
  item
}: {
  readonly item: AccountIngredient;
}) {
  return <li data-testid="formula-ingredient" className="xrvj5dj xju1xpo x1vdzsm xcfm6fe x1wiri7k xez8t42 x13fuv20 x178xt8z">
      <span className="x78zum5 x1a02dak x1pha0wt x1173whf xqu410z xeuugli">
        {item.name}
        {item.addon ? <span className="x1jty25b xox936i">Add-on</span> : null}
      </span>
      <span className="x1l1vl4p xqu410z">{formatDose(item.dose, item.unit)}</span>
      {item.reason !== "" && <p className="x1osaytk x1ghz6dp xl4dl4e x1jty25b x1fzhlzt">{item.reason}</p>}
    </li>;
}
function FormulaSheet({
  formula
}: {
  readonly formula: AccountFormula;
}) {
  const base = baseLine(formula);
  const reviewing = formula.status === "in_review";
  return <section {...stylex.props(paper, styles.sheet)} data-testid="current-formula">
      <div className="x78zum5 xdt5ytf xskm3c9">
        <p className="x1ghz6dp xl4dl4e x1jty25b x1l1vl4p">Version {String(formula.version)}</p>
        {base && <p className="x1ghz6dp x11mdwsw xqu410z x1d3j7mx x1e5flnj">{base}</p>}
      </div>
      <ul className="xe8uvvx x1ghz6dp x1717udv x78zum5 xdt5ytf" aria-label="Your formula">
        {formula.ingredients.map(item => <IngredientRow key={item.ingredientId} item={item} />)}
      </ul>
      <div className="x78zum5 xdt5ytf xskm3c9 xjh1bw0 xez8t42 x13fuv20 x178xt8z">
        {reviewing ? <>
            <p className="x1ghz6dp xqu410z">Waiting for your physician</p>
            <p className="x1ghz6dp xl4dl4e x1jty25b">They review and sign within 24 hours.</p>
          </> : <>
            <p className="x1ghz6dp xqu410z">{signedLine(formula)}</p>
            {formula.status === "approved" ? null : <p className="x1ghz6dp xl4dl4e x1jty25b">{statusLabel(formula.status)}</p>}
          </>}
      </div>
    </section>;
}
function VersionEvent({
  version,
  older
}: {
  readonly version: AccountFormulaVersion;
  readonly older: AccountFormulaVersion | undefined;
}) {
  const extras = [proteinNote(version, older), flavorNote(version, older)].filter(note => note !== null);
  return <li data-testid="formula-version" className="x78zum5 xdt5ytf x1173whf x4wraju x1wme0gj">
      <h3 className="x1ghz6dp xqu410z x1l1vl4p">Version {String(version.version)}</h3>
      <p className="x1ghz6dp xl4dl4e x1jty25b x1l1vl4p">
        {statusLabel(version.status)}, {day(version.createdAt)}
      </p>
      <p className="x1ghz6dp x1jty25b x1fzhlzt">{version.why}</p>
      {(version.changes.length > 0 || extras.length > 0) && <ul className="xe8uvvx x1ghz6dp x1717udv x78zum5 xdt5ytf xskm3c9" aria-label={`Changes in version ${String(version.version)}`}>
          {extras.map(note => <li key={note} data-testid="version-change" className="x1ghz6dp xl4dl4e x1fzhlzt">
              {note}
            </li>)}
          {version.changes.map(change => <li key={`${change.kind}-${change.ingredientId}`} data-testid="version-change" className="x1ghz6dp xl4dl4e x1fzhlzt">
              {changeLine(change)}
            </li>)}
        </ul>}
    </li>;
}
function VersionList({
  versions
}: {
  readonly versions: readonly AccountFormulaVersion[];
}) {
  return <aside className="x78zum5 xdt5ytf x1r0x45p xeuugli x1uhb9sk x17tt5qk x80663w xwmazx7" data-testid="formula-versions">
      <h2 className="x1ghz6dp x11mdwsw xqu410z x1d3j7mx x1e5flnj">Every version</h2>
      <ol className="xe8uvvx x1ghz6dp x1717udv x78zum5 xdt5ytf xv1tapc x19ypqd9 xe0pwq">
        {versions.map((version, index) => <VersionEvent key={version.id} version={version} older={versions[index + 1]} />)}
      </ol>
    </aside>;
}
function HomeView({
  home
}: {
  readonly home: AccountFormulas;
}) {
  const formula = home.current;
  if (formula === null) {
    return <div className="x78zum5 xdt5ytf x15agyyk xowlazn">
        <Text tone="secondary">
          You have no formula yet. Upload your labs to start. A physician reviews them within 24
          hours.
        </Text>
        <LinkButton href="/start">Upload your labs</LinkButton>
      </div>;
  }
  const reviewing = formula.status === "in_review";
  return <div className="xrvj5dj x1mkdm3x x1cmgb3p x7a106z xtmfs0b x3kykmy">
      <div className="x78zum5 xdt5ytf xd3upel xeuugli">
        <div className="x78zum5 xdt5ytf x626il7 x19pysoa">
          {reviewing ? <Status>A physician is reviewing this formula.</Status> : <Status state="ok">{signedLine(formula)}</Status>}
          {reviewing && <Text tone="secondary">We'll email you when they sign it.</Text>}
          <LinkButton href="/account/formula">Change this formula</LinkButton>
        </div>
        <FormulaSheet formula={formula} />
      </div>
      <VersionList versions={home.versions} />
    </div>;
}
export function Account() {
  const [home, setHome] = useState<Home>("loading");
  const load = () => {
    setHome("loading");
    void loadAccountFormulas().then(loaded => {
      if (loaded === "signed_out") setHome("signed_out");else if (loaded === null) setHome("failed");else setHome(loaded);
    }, () => setHome("failed"));
  };
  useEffect(load, []);
  if (home === "signed_out") {
    return <AccountShell title="Your formula" signedOut gateTitle="Sign in to see your formula" />;
  }
  return <AccountShell title={home === "failed" ? "We couldn't load your formula" : home !== "loading" && home.current === null ? "No formula yet" : "Your formula"} width="wide">
      <div data-testid="account-page" className="xeuugli">
        {home === "loading" ? <Pending message="Loading your formula" bones={["title", "line", "row", "row"]} /> : home === "failed" ? <div className="x78zum5 xdt5ytf x15agyyk xowlazn">
            <Text tone="secondary">
              Try again in a minute. If it keeps happening, write to support.
            </Text>
            <Button onClick={load}>Try again</Button>
          </div> : <HomeView home={home} />}
      </div>
    </AccountShell>;
}